import { FC } from 'react'
import { useLocation } from 'react-router-dom'
import { Button } from '@icanbwell/ui-toolkit'
import { Box, Typography, styled } from '@mui/material'
import ClientLogo from 'components/ClientLogo'
import { HEADER_HEIGHT } from '@client-hub/shared/constants/styles'
import { useTranslation } from 'react-i18next'

const Wrapper = styled(Box)({
  minHeight: `calc(100vh - ${HEADER_HEIGHT})`,
  maxWidth: '420px',
  paddingTop: '3rem',
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
})

const ListStyled = styled('ul')(({ theme }) => ({
  display: 'grid',
  padding: '1rem',
  gridTemplateColumns: '1fr 1fr',
  textAlign: 'center',
  background: theme.palette.grey['300'],
  li: {
    padding: '0.25rem 1rem',
    listStyle: 'none',
  },
}))

export type MfaBackupContentType = {
  showLogo?: boolean
}

type Props = {
  content?: MfaBackupContentType
  onContinue: () => void
}

const MfaBackup: FC<Props> = ({ content = {}, onContinue }) => {
  const { t } = useTranslation()

  const location = useLocation()
  const { backup_codes = [] } = (location.state as { backup_codes?: string[]; uid?: string }) || { state: {} }
  const csvContent = 'data:text/csv;charset=utf-8,' + backup_codes

  const copyBackupCodes = () => navigator.clipboard.writeText(backup_codes.toString())

  const downloadBackupCodes = () => {
    const link = document.createElement('a')
    link.href = encodeURI(csvContent)
    link.download = 'backup_codes.txt'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Wrapper>
      {content.showLogo && <ClientLogo variant="dark" />}
      <Typography
        component="h1"
        sx={{
          color: 'common.primaryBase',
          fontSize: '1.7rem',
          lineHeight: '2.1rem',
          margin: '2rem 0 1rem',
        }}
      >
        {t('routes.mfaBackup.title')}
      </Typography>
      <Box>{t('routes.mfaBackup.subtitle')}</Box>
      <ListStyled>
        {backup_codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ListStyled>
      <Box display="flex" justifyContent="space-between">
        <Button size="small" onClick={copyBackupCodes}>
          {t('routes.mfaBackup.copyCodes')}
        </Button>
        <Button size="small" variant="outlined" onClick={downloadBackupCodes}>
          {t('routes.mfaBackup.downloadCodes')}
        </Button>
      </Box>
      <Button style={{ margin: '2rem auto' }} onClick={onContinue}>
        {t('routes.mfaBackup.continue')}
      </Button>
    </Wrapper>
  )
}

export default MfaBackup
